import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SERVICES } from '../../config/services';
import { trackEvent } from '../../utils/analytics';

const ServiceHub = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleServiceClick = (service) => {
    const featureType = service.id === 'face-analysis' ? 'face' : (service.id === 'chemistry-test' ? 'chemistry' : undefined);
    trackEvent('feature_selected', {
      feature_name: service.id.replaceAll('-', '_'),
      feature_type: featureType,
      source_page: 'home_service_hub',
    });
    navigate(service.route);
  };

  return (
    <section className="service-hub">
      <div className="hub-header">
        <h2>{t('home.hero.title')}</h2>
        <p>{t('home.hero.subtitle')}</p>
      </div>

      {/* 외형 분석 섹션 */}
      <div className="category-section">
        <div className="category-header">
          <h3>{t('home.category.appearance.title')}</h3>
          <p>{t('home.category.appearance.description')}</p>
        </div>
        <div className="service-grid">
          {SERVICES.filter(s => s.category === 'appearance').map((service) => (
            <div key={service.id} className="service-card" onClick={() => handleServiceClick(service)}>
              <div className="card-image-placeholder">
                {service.id === 'style-check' ? <div className="service-style-preview"><span>AI STYLE REPORT</span><strong>82<small>/100</small></strong><p>{t('home.style_preview.tip')}</p><div><i>{t('home.style_preview.adjust')}</i><i>{t('home.style_preview.keep')}</i></div></div> : <span className="service-icon">{service.icon}</span>}
              </div>
              <div className="card-content">
                <div className="badge-row">
                  <span className="service-badge">{service.badge}</span>
                </div>
                <h3>{t(`service.${service.id}.name`)}</h3>
                <p>{t(`service.${service.id}.description`)}</p>
                <button className="card-cta">{t('common.start')}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 내면 분석 섹션 */}
      <div className="category-section">
        <div className="category-header">
          <h3>{t('home.category.inner.title')}</h3>
          <p>{t('home.category.inner.description')}</p>
        </div>
        <div className="service-grid">
          {SERVICES.filter(s => s.category === 'inner').map((service) => (
            <div key={service.id} className="service-card" onClick={() => handleServiceClick(service)}>
              <div className="card-image-placeholder">
                {service.id === 'style-check' ? <div className="service-style-preview"><span>AI STYLE REPORT</span><strong>82<small>/100</small></strong><p>{t('home.style_preview.tip')}</p><div><i>{t('home.style_preview.adjust')}</i><i>{t('home.style_preview.keep')}</i></div></div> : <span className="service-icon">{service.icon}</span>}
              </div>
              <div className="card-content">
                <div className="badge-row">
                  <span className="service-badge">{service.badge}</span>
                </div>
                <h3>{t(`service.${service.id}.name`)}</h3>
                <p>{t(`service.${service.id}.description`)}</p>
                <button className="card-cta">{t('common.start')}</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .service-hub {
          max-width: var(--container-max);
          margin: 0 auto;
          padding: 6rem max(12px, 4vw);
        }

        .hub-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .hub-header h2 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .hub-header p {
          color: var(--text-muted);
          font-size: 1.125rem;
        }

        .category-section {
          margin-bottom: 5rem;
        }

        .category-header {
          margin-bottom: 2rem;
          padding-left: 1rem;
          border-left: 4px solid var(--primary);
        }

        .category-header h3 {
          font-size: 1.8rem;
          color: #fff;
          margin-bottom: 0.5rem;
        }

        .category-header p {
          color: var(--text-muted);
          font-size: 1rem;
          margin: 0;
        }

        .service-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          justify-content: center;
        }

        .service-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 20px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .service-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
          border-color: var(--primary);
        }

        .card-image-placeholder {
          height: 200px;
          background: linear-gradient(135deg, #1f2937, #111827);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 4rem;
        }

        .service-style-preview { width: calc(100% - 36px); padding: 15px; border: 1px solid rgba(209,188,255,.25); border-radius: 14px; background: radial-gradient(circle at 80% 0,#433552,#17131c 70%); text-align: left; }
        .service-style-preview>span { color:#cdb7ff;font-size:9px;font-weight:900;letter-spacing:.12em; }
        .service-style-preview>strong { display:block;margin:7px 0 4px;color:#fff;font-size:32px;line-height:1; }
        .service-style-preview>strong small { font-size:11px;color:#8f8994; }
        .service-style-preview>p { margin:0 0 11px;color:#c9c2cd;font-size:11px;line-height:1.4; }
        .service-style-preview>div { display:flex;gap:6px; }
        .service-style-preview i { padding:4px 6px;border-radius:5px;color:#dacaff;background:#30273a;font-size:9px;font-style:normal; }

        .card-content {
          padding: 2rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .badge-row {
          margin-bottom: 1rem;
        }

        .service-badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.75rem;
          border-radius: 100px;
          background: rgba(59, 130, 246, 0.1);
          color: var(--primary);
          font-weight: 600;
          text-transform: uppercase;
        }

        h3 {
          font-size: 1.5rem;
          margin-bottom: 0.75rem;
          color: #fff;
        }

        p {
          color: var(--text-muted);
          font-size: 0.95rem;
          line-height: 1.6;
          margin-bottom: 2rem;
          flex: 1;
        }

        .card-cta {
          width: 100%;
          padding: 1rem;
          background: transparent;
          border: 1px solid var(--border-color);
          color: #fff;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.2s;
        }

        .service-card:hover .card-cta {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
        }
      `}</style>
    </section>
  );
};

export default ServiceHub;
